import {
    JsonApiDocument,
    JsonApiResource,
    ModelForType,
    SchemaCollection,
    Store,
} from 'json-api-models';
import { reactive } from 'vue';

import type { components } from '../openapi.ts';
import { withoutStaleFields } from './composables/request/writes.ts';
import { Activity } from './models/activity.ts';
import { AgentConversation, AgentConversationMessage } from './models/agentConversation.ts';
import { Bookmark } from './models/bookmark.ts';
import { BulkAction } from './models/bulkAction.ts';
import { CohortResult } from './models/cohortResult.ts';
import { Collection } from './models/collection.ts';
import { CollectionProgress } from './models/collectionProgress.ts';
import { CollectionRevision } from './models/collectionRevision.ts';
import { Commission } from './models/commission.ts';
import { Contribution } from './models/contribution.ts';
import { ContributionTier } from './models/contributionTier.ts';
import { ContributionType } from './models/contributionType.ts';
import { Coupon } from './models/coupon.ts';
import { Curriculum } from './models/curriculum.ts';
import { Entitlement } from './models/entitlement.ts';
import { Exam } from './models/exam.ts';
import { Feedback } from './models/feedback.ts';
import { FeedbackPreset } from './models/feedbackPreset.ts';
import { Group } from './models/group.ts';
import { Highlight } from './models/highlight.ts';
import { Institution } from './models/institution.ts';
import { Item } from './models/item.ts';
import { ItemRevision } from './models/itemRevision.ts';
import { ItemRevisionGeneration } from './models/itemRevisionGeneration.ts';
import { Media } from './models/media.ts';
import { ModuleProgress } from './models/moduleProgress.ts';
import { Note } from './models/note.ts';
import { Notification } from './models/notification.ts';
import { Pack } from './models/pack.ts';
import { PackGroup } from './models/packGroup.ts';
import { Presentation } from './models/presentation.ts';
import { PresentationQuestion } from './models/presentationQuestion.ts';
import { Product } from './models/product.ts';
import { ProductGoal } from './models/productGoal.ts';
import { Question } from './models/question.ts';
import { QuestionAttempt } from './models/questionAttempt.ts';
import { QuestionHistory } from './models/questionHistory.ts';
import { QuestionProgress } from './models/questionProgress.ts';
import { QuestionRevision } from './models/questionRevision.ts';
import { QuestionSession } from './models/questionSession.ts';
import { QuestionStatistic } from './models/questionStatistic.ts';
import { Referral } from './models/referral.ts';
import { Roadmap } from './models/roadmap.ts';
import { Role } from './models/role.ts';
import { Station } from './models/station.ts';
import { StationAttempt } from './models/stationAttempt.ts';
import { StationRevision } from './models/stationRevision.ts';
import { Subscription } from './models/subscription.ts';
import { SubscriptionPayment } from './models/subscriptionPayment.ts';
import { Tag } from './models/tag.ts';
import { TagGroup } from './models/tagGroup.ts';
import { TagResult } from './models/tagResult.ts';
import { Template } from './models/template.ts';
import { Tutorial } from './models/tutorial.ts';
import { TutorialPage } from './models/tutorialPage.ts';
import { TutorialRevision } from './models/tutorialRevision.ts';
import { TutorialSession } from './models/tutorialSession.ts';
import { UsageReport } from './models/usageReport.ts';
import { User } from './models/user.ts';
import { Video } from './models/video.ts';
import { VideoProgress } from './models/videoProgress.ts';
import { VideoRevision } from './models/videoRevision.ts';
import { Webinar } from './models/webinar.ts';

export * from './models/activity.ts';
export * from './models/agentConversation.ts';
export * from './models/bookmark.ts';
export * from './models/bulkAction.ts';
export * from './models/cohortResult.ts';
export * from './models/collection.ts';
export * from './models/collectionProgress.ts';
export * from './models/collectionRevision.ts';
export * from './models/commission.ts';
export * from './models/contribution.ts';
export * from './models/contributionTier.ts';
export * from './models/contributionType.ts';
export * from './models/coupon.ts';
export * from './models/curriculum.ts';
export * from './models/entitlement.ts';
export * from './models/exam.ts';
export * from './models/feedback.ts';
export * from './models/feedbackPreset.ts';
export * from './models/group.ts';
export * from './models/highlight.ts';
export * from './models/institution.ts';
export * from './models/item.ts';
export * from './models/itemRevision.ts';
export * from './models/itemRevisionGeneration.ts';
export * from './models/media.ts';
export * from './models/moduleProgress.ts';
export * from './models/note.ts';
export * from './models/notification.ts';
export * from './models/pack.ts';
export * from './models/packGroup.ts';
export * from './models/presentation.ts';
export * from './models/presentationQuestion.ts';
export * from './models/product.ts';
export * from './models/productGoal.ts';
export * from './models/question.ts';
export * from './models/questionAttempt.ts';
export * from './models/questionHistory.ts';
export * from './models/questionProgress.ts';
export * from './models/questionRevision.ts';
export * from './models/questionSession.ts';
export * from './models/questionStatistic.ts';
export * from './models/referral.ts';
export * from './models/roadmap.ts';
export * from './models/role.ts';
export * from './models/station.ts';
export * from './models/stationAttempt.ts';
export * from './models/stationRevision.ts';
export * from './models/subscription.ts';
export * from './models/subscriptionPayment.ts';
export * from './models/tag.ts';
export * from './models/tagGroup.ts';
export * from './models/tagResult.ts';
export * from './models/template.ts';
export * from './models/tutorial.ts';
export * from './models/tutorialPage.ts';
export * from './models/tutorialRevision.ts';
export * from './models/tutorialSession.ts';
export * from './models/usageReport.ts';
export * from './models/user.ts';
export * from './models/video.ts';
export * from './models/videoProgress.ts';
export * from './models/videoRevision.ts';
export * from './models/webinar.ts';

export type Schemas = components['schemas'];

const modelMap = {
    activity: Activity,
    agentConversationMessages: AgentConversationMessage,
    agentConversations: AgentConversation,
    bookmarks: Bookmark,
    bulkActions: BulkAction,
    cohortResults: CohortResult,
    collectionProgress: CollectionProgress,
    collectionRevisions: CollectionRevision,
    collections: Collection,
    commissions: Commission,
    contributions: Contribution,
    contributionTiers: ContributionTier,
    contributionTypes: ContributionType,
    coupons: Coupon,
    curricula: Curriculum,
    entitlements: Entitlement,
    exams: Exam,
    feedback: Feedback,
    feedbackPresets: FeedbackPreset,
    groups: Group,
    highlights: Highlight,
    institutions: Institution,
    itemRevisionGenerations: ItemRevisionGeneration,
    itemRevisions: ItemRevision,
    items: Item,
    media: Media,
    moduleProgress: ModuleProgress,
    notes: Note,
    notifications: Notification,
    packGroups: PackGroup,
    packs: Pack,
    presentationQuestions: PresentationQuestion,
    presentations: Presentation,
    productGoals: ProductGoal,
    products: Product,
    questionAttempts: QuestionAttempt,
    questionHistory: QuestionHistory,
    questionProgress: QuestionProgress,
    questionRevisions: QuestionRevision,
    questions: Question,
    questionSessions: QuestionSession,
    questionStatistics: QuestionStatistic,
    referrals: Referral,
    roadmaps: Roadmap,
    roles: Role,
    stationAttempts: StationAttempt,
    stationRevisions: StationRevision,
    stations: Station,
    subscriptionPayments: SubscriptionPayment,
    subscriptions: Subscription,
    tagGroups: TagGroup,
    tagResults: TagResult,
    tags: Tag,
    templates: Template,
    tutorialPages: TutorialPage,
    tutorialRevisions: TutorialRevision,
    tutorials: Tutorial,
    tutorialSessions: TutorialSession,
    usageReports: UsageReport,
    users: User,
    videoProgress: VideoProgress,
    videoRevisions: VideoRevision,
    videos: Video,
    webinars: Webinar,
};

export type Models = {
    [Type in keyof typeof modelMap]: InstanceType<(typeof modelMap)[Type]>;
};

class ReactiveStore<Schemas extends SchemaCollection> extends Store<Schemas> {
    protected graph: { [type: string]: { [id: string]: any } } = reactive({});

    // Override the store to make model reactivity work. When models are
    // constructed with their initial data, getters are set up for attributes
    // and relationships, but these point to the original non-reactive object.
    // So we re-merge the data on the reactive proxy so the getters will be fixed.
    public createModel<Type extends (keyof Schemas & string) | (string & {})>(
        data: JsonApiResource<Type>,
    ): ModelForType<Type, Schemas> {
        const model = reactive(super.createModel({ type: data.type, id: data.id })) as ModelForType<
            Type,
            Schemas
        >;

        model.merge(data);

        return model;
    }

    // Leave out fields a response would overwrite newer local writes with, so
    // every sync of a response, however it is reached, keeps them.
    public sync<Type extends (keyof Schemas & string) | (string & {})>(
        document: JsonApiDocument<Type>,
    ) {
        return super.sync(withoutStaleFields(document) as JsonApiDocument<Type>);
    }
}

export const models = new ReactiveStore(modelMap);

(window as any).models = models;
